import { Resource } from "../../models/resource";

/**
 * Help to manage the complex parameter when listing resources,
 * This class check if the parent component have the following methods:
 *
 * - getCustomListingParams()   : if this method exists, the object returned by this method will be copied
 *                                to the default listing parameters
 *
 * - preProcessCollection()     : if this method exists, the record collection from database will be passed to this method
 *                                so the parent can do some pre-processing before TableHelper will display the data.
 *                                This method MUST return the collection and NOT try to display it directly
 */
export class TableHelper {

    /**
     * @param options
     *
     * This parameter should have the following structure:
     *
     * let options = {
     *     parent: component,
     *     columns: ['id', 'name'],
     *     endpoint: '/v2/fabrics/suppliers',
     *     perPage: 10,
     *     filterSelector: '#selector',
     *     filterColumns: ['name', 'code']
     *     sort: {
     *         id: 'asc',
     *         created_at: 'desc'
     *     }
     * };
     *
     * options.parent           : The parent VueJS component, generally, this will be equal to *this* as the helper
     *                            is created inside the parent itself.
     * options.columns          : The list of column to fetch from database.
     * options.endpoint         : The endpoint of the resource route as described in the file [docs/backend-refactoring.md].
     * options.perPage          : The number of row per page
     * options.filterSelector   : The selector of the filter input
     * options.filterColumns    : List of column to filter. Must match the column names of the resource in the database.
     *
     */
    constructor(options) {
        this.parent = options.parent;
        this.filters = options.filterColumns ? options.filterColumns : [];
        this.filterValue = '';
        this.sort = new Map();
        this.clientSortOnly = options.clientSortOnly ? options.clientSortOnly : [];
        this.endpoint = options.endpoint;
        this.columns = options.columns ? options.columns : ['*'];
        this.page = 1;
        this.perPage = options.perPage ? options.perPage : 10;
        this.targetTable = null;
        this.watchVueTables();
        this.initFilter(options);
        this.initSort(options);
        this.reloadData();
    }

    initSort(options) {
        if (options.sort) {
            for (const [key, value] of Object.entries(options.sort)) {
                this.sort.set(key, value);
            }
        }
    }

    replaceSort(options) {
        this.sort = new Map();
        this.initSort(options);
    }

    /**
     * Setup the input filter
     * - find the input filter with the given selector
     * - add ENTER event listener
     * @param options
     */
    initFilter(options) {
        this.filter = null;
        if (options.filterSelector) {
            if (options.filterSelector instanceof Element) {
                this.filter = options.filterSelector;
            } else {
                this.filter = document.querySelector(options.filterSelector);
            }
            this.filter.addEventListener('keyup', this.onFilterChanged.bind(this));
        }
    }

    /**
     * Reload the table data
     */
    reloadData() {
        let parent = this.parent;
        Resource.fetchCollection(this.endpoint, this.getListingParams()).then(response => {
            if (response.status === 'OK') {
                if (this.page === 1 && this.targetTable !== undefined) {
                    this.targetTable.reset();
                }
                if (typeof parent.preProcessCollection === 'function') {
                    parent.rows = parent.preProcessCollection(response.data.collection);
                } else {
                    parent.rows = response.data.collection;
                }
                parent.totalRecords = response.data.pagination.total_records;
            }
        });
    }

    findComponentsByName(name) {
        let components = [];
        let parentChildren = this.parent.$children;
        if (parentChildren.length > 0) {
            for (let i = 0; i < parentChildren.length; i++) {
                let options = parentChildren[i].$vnode.componentOptions;
                if (options.tag === name) {
                    components.push(parentChildren[i]);
                }
            }
        }
        return components;
    }

    /**
     * Listen to events on all VueGoodTable
     */
    watchVueTables() {
        let components = this.findComponentsByName('vue-good-table');
        if (components.length > 0) {
            this.targetTable = components[0];
            this.addVueTableListeners(this.targetTable);
        }
        else {
            console.warn('no vue-good-table found');
        }
    }

    /**
     *
     * @param table
     */
    addVueTableListeners(table) {
        table.$on('on-sort-change', this.onSortChanged.bind(this));
        table.$on('on-page-change', this.onPageChanged.bind(this));
        table.$on('on-per-page-change', this.onPerPageChanged.bind(this));
        // table.$on('on-column-filter', this.onFilterChanged.bind(this));
    }

    /**
     * Handle
     * @param params
     */
    onSortChanged(params) {
        if (!this.clientSortOnly.includes(params[0].field)) {
            this.sort.clear();
            this.sort.set(params[0].field, params[0].type);
            this.page = 1;
            this.reloadData();
        }
    }

    onPageChanged(params) {
        this.page = params.currentPage;
        console.log(params)
        this.reloadData();
    }

    onPerPageChanged(params) {
        this.page = 1;
        this.perPage = params.currentPerPage;
        this.reloadData();
    }

    onFilterChanged(event) {
        if (event.key === 'Enter') {
            this.page = 1;
            this.filterValue = this.filter.value;
            this.reloadData();
        }
    }

    getListingParams() {

        // Get sort
        let sorts = [];
        this.sort.forEach((type, column) => {
            sorts.push(column + ':' + type);
        });

        // Get filter
        let filters = '';
        let search = '';
        if (this.filterValue !== '' && this.filters.length > 0) {
            filters = this.filters.join(',');
            search = this.filterValue;
        }

        let listingParams = {
            columns: this.columns.join(','),
            filters: filters,
            search: search,
            sort: sorts.join(','),
            page: this.page,
            per_page: this.perPage
        };

        if (typeof this.parent.getCustomListingParams === 'function') {
            listingParams = Object.assign(listingParams, this.parent.getCustomListingParams());
        }

        return listingParams;
    }
}
